import { inject, Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { forkJoin, map, Observable, of, take, tap } from 'rxjs';
import { ModulesStore } from '../../../../features/admin/modules/modules.store';
import { FormService } from '../../../../features/admin/modules/features/form-builder/form.service';
import { BASED_ON_SELECTED_FIELD, createFieldWithOverrides } from '../../../utils/formly-field.util';
import { cloneDeep } from 'lodash-es';
import type { Module, OptionsItem, SourceField } from '../../../../features/admin/modules/modules.model';
import { EntriesService } from '../../../../features/admin/modules/features/entries/entries.service';
import { UtilityService } from '../../../services/utility.service';
import { UserRolesStore } from '../../../../features/admin/user-roles/user-roles.store';
import { FormHelper } from '../../../../features/admin/modules/features/form-builder/form.util';
import type { FieldOption } from '../../../../features/admin/modules/features/form-builder/form.model';
import { MappingExpressionService } from '../../../services/mapping-expression.service';

@Injectable({
  providedIn: 'root',
})
export class ValueMappingFieldService {
  private entriesService = inject(EntriesService);
  private userRolesStore = inject(UserRolesStore);
  private modulesStore = inject(ModulesStore);
  private formService = inject(FormService);
  private mappingExpressionService = inject(MappingExpressionService);

  showFormbuilderField = false;

  /** Parse errors per expression string, so the async validator can surface the real JSONata message. */
  readonly #expressionErrors = new Map<string, string>();

  /**
   * Generates field configuration for value mapping
   */
  getValueMappingFields(module: Module, basedOptions = this.getBasedOnOptions(module)): FormlyFieldConfig {
    return {
      type: 'repeat',
      props: {
        label: 'Waardes voor item',
        description: '',
        addText: 'Waarde toevoegen',
        placeholder: '',
      },
      fieldArray: {
        fieldGroup: [
          this.getTargetFieldConfig(),
          createFieldWithOverrides(BASED_ON_SELECTED_FIELD, {
            defaultValue: 'dynamic',
            className: 'pl-4',
            props: {
              label: 'Beoordeel op',
              options: basedOptions,
            },
            expressions: {
              hide: (field: FormlyFieldConfig) => {
                const parentAction = field.parent?.parent?.parent?.parent?.model;
                return parentAction?.typeOption === 'calculateField';
              },
            },
          }),
          {
            key: 'basedOn',
            type: 'select',
            className: 'w-max-sm',
            props: {
              label: 'Berekeningstype',
              placeholder: 'Selecteer berekeningstype',
              options: [
                { value: 'SUM', label: 'Som' },
                { value: 'AVERAGE', label: 'Gemiddelde' },
                { value: 'LAST', label: 'Laatste waarde' },
                { value: 'COUNT', label: 'Aantal gevonden entries' },
              ],
              required: true,
            },
            expressions: {
              hide: (field: FormlyFieldConfig) => {
                const parentAction = field.parent?.parent?.parent?.parent?.model;
                return parentAction?.typeOption !== 'calculateField';
              },
            },
          },
          ...this.getDynamicFieldConfiguration(module),
          this.getUserInputOptionalFieldConfig(),
          ...this.getValueFields(),
        ],
      },
    };
  }

  /**
   * Returns the dynamic field configuration based on form source type
   */
  getDynamicFieldConfiguration(module: Module): FormlyFieldConfig[] {
    // Only show form source selector if onboarding forms are available
    if (this.showFormbuilderField) {
      return [
        this.getFormSourceTypeSelector(),
        ...this.getStandardFormFields(module),
        ...this.getOnboardingFormFields(module),
        ...this.getExpressionFieldConfig(),
      ];
    }

    // If no onboarding forms available, show standard fields directly
    return [
      this.getSourceTypeFieldConfig(module),
      ...this.getSourceFieldConfig(module),
      this.getSourceSubFieldConfig(module),
      ...this.getStepResultFieldConfig(),
      ...this.getExpressionFieldConfig(),
    ];
  }

  /**
   * Editor rows for basedOn:'stepResult' — used by the Create-Form wizard runner to read a value
   * from an entry created in an earlier wizard step. `resultKey` matches the step's resultKey/key;
   * `sourceField` is the keyPath into that created entry. Both are gated on basedOn==='stepResult'.
   */
  getStepResultFieldConfig(): FormlyFieldConfig[] {
    const hideUnlessStepResult = (field: FormlyFieldConfig) =>
      this.formService.findFieldValue(field, 'basedOn') !== 'stepResult';

    return [
      {
        key: 'resultKey',
        type: 'input',
        className: 'pl-4',
        props: {
          label: 'Resultaat-sleutel eerdere stap',
          description: 'De sleutel van de wizard-stap waarvan de aangemaakte entry wordt gelezen.',
          placeholder: 'bijv. bedrijf',
        },
        expressions: {
          hide: hideUnlessStepResult,
        },
      },
      {
        key: 'sourceField',
        type: 'input',
        className: 'pl-4',
        props: {
          label: 'Pad waarde (eerdere stap)',
          description: 'Het pad binnen de aangemaakte entry, bijvoorbeeld id of entry.general.naam.',
          placeholder: 'id',
        },
        expressions: {
          hide: hideUnlessStepResult,
        },
      },
    ];
  }

  /** Editor row for basedOn:'userInput' — lets the popup be confirmed with this field left empty. */
  getUserInputOptionalFieldConfig(): FormlyFieldConfig {
    return {
      key: 'optional',
      type: 'toggle',
      className: 'pl-4',
      defaultValue: false,
      props: {
        label: 'Optioneel invullen',
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => this.formService.findFieldValue(field, 'basedOn') !== 'userInput',
      },
    };
  }

  /**
   * Editor row for basedOn:'expression' — a JSONata expression evaluated against the source entry.
   * Interface actions only: the backend action-executor does not evaluate expressions, so this row
   * is only reachable when the caller opts in via `getBasedOnOptions(module, 'interface', true)`.
   */
  getExpressionFieldConfig(): FormlyFieldConfig[] {
    return [
      {
        key: 'expression',
        type: 'textarea',
        className: 'pl-4',
        props: {
          label: 'Expressie (JSONata)',
          required: true,
          description:
            'Wordt uitgerekend op de bron-entry: bij bewerken = het doel-item, bij aanmaken = de rij ' +
            'waarop de knop staat. Veldnamen met een streepje tussen accent graves. Ook beschikbaar: ' +
            '$trigger (rij van de knop), $stepResults, $user, $company, $location, $now. ' +
            'Voorbeeld: entry.general.name ? entry.general.name : entry.general.`vehicle-general`.`license-plate`.plate',
          placeholder: 'entry.general.name ? entry.general.name : $trigger.entry.general.code',
          rows: 3,
        },
        expressions: {
          // Hidden for other basedOn types; a hidden required field is skipped by Formly validation,
          // so `required` only bites when this row is actually shown (basedOn === 'expression').
          hide: (field: FormlyFieldConfig) => this.formService.findFieldValue(field, 'basedOn') !== 'expression',
        },
        asyncValidators: {
          expressionSyntax: {
            expression: async (control: AbstractControl) => {
              const value: string = control.value ?? '';
              const error = await this.mappingExpressionService.validate(value);
              if (error) {
                this.#expressionErrors.set(value, error);
              } else {
                this.#expressionErrors.delete(value);
              }
              return !error;
            },
            message: (_error: unknown, field: FormlyFieldConfig) =>
              this.#expressionErrors.get(field.formControl?.value ?? '') ?? 'Ongeldige expressie',
          },
        },
      },
    ];
  }

  /**
   * Form source type selector - choose between standard form or onboarding form
   */
  private getFormSourceTypeSelector(): FormlyFieldConfig {
    return {
      key: 'formSourceType',
      type: 'select',
      defaultValue: 'standard',
      props: {
        label: 'Formulier type',
        description: 'Kies tussen standaard module formulier of een aanmeldingsformulier',
        options: [
          { value: 'standard', label: 'Standaard module formulier' },
          { value: 'onboarding', label: 'Aanmeldingsformulier' },
        ],
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => {
          const basedField = this.formService.findFieldValue(field, 'basedOn');
          return basedField !== 'dynamic';
        },
      },
    };
  }

  /**
   * Returns standard form fields configuration
   */
  private getStandardFormFields(module: Module): FormlyFieldConfig[] {
    return [
      this.getSourceTypeFieldConfig(module),
      ...this.getSourceFieldConfig(module),
      this.getSourceSubFieldConfig(module),
    ].map(field => ({
      ...field,
      expressions: {
        ...field.expressions,
        hide: (fieldConfig: FormlyFieldConfig) => {
          // First check if formSourceType is 'onboarding'
          const formSourceType = this.formService.findFieldValue(fieldConfig, 'formSourceType');
          if (formSourceType === 'onboarding') {
            return true;
          }

          // Then apply original hide logic if it exists and is a function
          const originalHide = field.expressions?.hide;
          if (typeof originalHide === 'function') {
            return originalHide(fieldConfig);
          }

          // If hide is a boolean or string, evaluate it
          if (typeof originalHide === 'boolean') {
            return originalHide;
          }
          return false;
        },
      },
    }));
  }

  /**
   * Returns onboarding form fields configuration
   */
  private getOnboardingFormFields(module: Module): FormlyFieldConfig[] {
    return this.getDynamicFormBuilderOutputFieldConfig(module).map(field => ({
      ...field,
      expressions: {
        ...field.expressions,
        hide: (fieldConfig: FormlyFieldConfig) => {
          // First check if formSourceType is 'standard'
          const formSourceType = this.formService.findFieldValue(fieldConfig, 'formSourceType');
          if (formSourceType !== 'onboarding') {
            return true;
          }

          // Then apply original hide logic if it exists and is a function
          const originalHide = field.expressions?.hide;
          if (typeof originalHide === 'function') {
            return originalHide(fieldConfig);
          }

          // If hide is a boolean or string, evaluate it
          if (typeof originalHide === 'boolean') {
            return originalHide;
          }
          return false;
        },
      },
    }));
  }

  getTargetFieldConfig(): FormlyFieldConfig {
    return {
      key: 'targetField',
      type: 'autocomplete',
      props: {
        label: 'Selecteer invoerveld (waarde instellen)',
        description: 'Selecteer invoerveld waar je een standaard waarde wil instellen',
        placeholder: '',
        options: of([]),
        requireSelection: false,
      },
      expressions: {
        'props.options': (field: FormlyFieldConfig) => {
          if (field.hide) return [];

          const excludeTypes: string[] = ['pages', 'tabs', 'list', 'linked-items', 'required-items'];
          const moduleCode = this.getModuleCode(field);

          const selectedModule = this.modulesStore.modules().find(module => module.code === moduleCode);
          if (!selectedModule) return [];

          return this.formService.getFieldsOptionsOfModule(
            this.modulesStore.generatedForms()[selectedModule.id],
            excludeTypes,
            true,
            this.modulesStore.generatedForms(),
            this.modulesStore.moduleCodeMapper()
          );
        },
      },
    };
  }

  getDynamicFormBuilderOutputFieldConfig(module: Module): FormlyFieldConfig[] {
    return [
      {
        key: 'formBuilderForm',
        type: 'select',
        className: 'pl-4',
        props: {
          label: 'Selecteer formulier die gebruikt moet worden',
          description: '',
          placeholder: '',
          options: [],
        },
        expressions: {
          hide: (field: FormlyFieldConfig) => {
            const basedField = this.formService.findFieldValue(field, 'basedOn');
            return basedField !== 'dynamic';
          },
          'props.options': (field: FormlyFieldConfig) => {
            if (field.hide) return [];

            const onboardingModule = this.modulesStore.getModuleByCode('onboarding-forms');
            return this.entriesService.getEntries({ moduleId: onboardingModule.id, offset: 0, limit: 1000 }).pipe(
              map(entries => {
                return entries.map(entry => ({
                  label: UtilityService.keyPathToValue(entry, onboardingModule.titelKeyPath),
                  value: entry.id,
                }));
              })
            );
          },
        },
        hooks: {
          onInit: (field: FormlyFieldConfig) => {
            const onboardingModule = this.modulesStore.getModuleByCode('onboarding-forms');
            const options = this.entriesService
              .getEntries({ moduleId: onboardingModule.id, offset: 0, limit: 1000 })
              .pipe(
                map(entries => {
                  return entries.map(entry => ({
                    label: UtilityService.keyPathToValue(entry, onboardingModule.titelKeyPath),
                    value: entry.id,
                  }));
                })
              );
            options.subscribe(options => {
              field.props.options = options;
            });
          },
        },
      },
      {
        key: 'sourceField',
        type: 'autocomplete',
        className: 'pl-4',
        props: {
          label: 'Selecteer invoerveld (waarde overnemen)',
          description: '',
          placeholder: '',
          options: [],
          hideIfOptionsEmpty: false,
          requireSelection: true,
        },
        expressions: {
          hide: (field: FormlyFieldConfig) => {
            if (field.model?.formBuilderForm == null) {
              return true;
            }
            const basedField = this.formService.findFieldValue(field, 'basedOn');
            return basedField !== 'dynamic';
          },
          'props.options': (field: FormlyFieldConfig) => {
            if (field.hide) return [];

            const onboardingModule = this.modulesStore.getModuleByCode('onboarding-forms');
            const formId = field.model.formBuilderForm;
            return this.entriesService.getEntry(onboardingModule.id, formId).pipe(
              map(entry => {
                const generatedForm = entry.entry.form.generatedForm;
                const excludeTypes: string[] = ['pages', 'tabs', 'list', 'linked-items', 'required-items'];
                return this.formService.getFieldsOptionsOfModule(
                  generatedForm,
                  excludeTypes,
                  true,
                  this.modulesStore.generatedForms(),
                  this.modulesStore.moduleCodeMapper()
                );
              })
            );
          },
        },
        hooks: {
          onInit: (field: FormlyFieldConfig) => {
            if (field.hide) return [];

            const excludeTypes: string[] = ['pages', 'tabs', 'list', 'linked-items', 'required-items'];
            const onboardingModule = this.modulesStore.getModuleByCode('onboarding-forms');
            field.props.options = this.entriesService.getEntry(onboardingModule.id, field.model.formBuilderForm).pipe(
              map(entry => {
                const generatedForm = entry.entry.form.generatedForm;
                return this.formService.getFieldsOptionsOfModule(
                  generatedForm,
                  excludeTypes,
                  true,
                  this.modulesStore.generatedForms(),
                  this.modulesStore.moduleCodeMapper()
                );
              })
            );
          },
        },
      },
    ];
  }

  getSourceTypeFieldConfig(module: Module): FormlyFieldConfig {
    return {
      key: 'sourceTypeField',
      type: 'select',
      defaultValue: 'field',
      className: 'pl-4',
      props: {
        label: 'Selecteer invoerveld type',
        description: '',
        placeholder: '',
        options: [
          {
            label: 'Invoerveld',
            value: 'field',
          },
          {
            label: 'Handmatige waarde',
            value: 'manual',
          },
        ],
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => {
          const basedField = this.formService.findFieldValue(field, 'basedOn');
          return basedField !== 'dynamic' && basedField !== 'append' && basedField !== 'subtract';
        },
      },
    };
  }

  getSourceFieldConfig(module: Module): FormlyFieldConfig[] {
    return [
      {
        key: 'sourceField',
        type: 'autocomplete',
        defaultValue: 'field',
        className: 'pl-4',
        props: {
          label: 'Selecteer invoerveld (waarde overnemen)',
          description: '',
          placeholder: '',
          options: of([]),
          requireSelection: true,
        },
        expressions: {
          hide: (field: FormlyFieldConfig) => {
            // stepResult uses its own dedicated sourceField (path) editor below, so hide this one
            // to avoid two visible editors writing the same `sourceField` key. Expressions carry
            // their whole source in the expression itself and need no field picker at all.
            const basedOn = this.formService.findFieldValue(field, 'basedOn');
            if (basedOn === 'stepResult' || basedOn === 'expression') {
              return true;
            }
            const basedField = this.formService.findFieldValue(field, 'sourceTypeField');
            return basedField !== 'field';
          },
          'props.options': (field: FormlyFieldConfig) => {
            if (field.hide) return [];

            const excludeTypes: string[] = ['pages', 'tabs', 'list', 'linked-items', 'required-items'];

            const options = this.formService.getFieldsOptionsOfModule(
              this.modulesStore.generatedForms()[module.id],
              excludeTypes,
              true,
              this.modulesStore.generatedForms(),
              this.modulesStore.moduleCodeMapper()
            );
            options.unshift({ label: 'Geen', value: '', type: '' });
            return options;
          },
        },
      },
      {
        key: 'sourceField',
        type: 'input',
        props: {
          label: 'Pad waarde',
          description: '',
          placeholder: 'entry.general.title',
          options: [],
        },
        expressions: {
          hide: (field: FormlyFieldConfig) => {
            const basedField = this.formService.findFieldValue(field, 'sourceTypeField');
            return basedField !== 'manual';
          },
        },
      },
      {
        key: 'sourceField',
        type: 'autocomplete',
        className: 'w-full',
        props: {
          label: 'Bronveld voor berekening',
          placeholder: 'Selecteer het veld om te berekenen',
          options: [],
          required: true,
          requireSelection: true,
          description: 'Het veld waarop de berekening uitgevoerd moet worden',
        },
        expressions: {
          hide: (field: FormlyFieldConfig) => {
            const parentAction = field.parent?.parent?.parent?.parent?.model;
            const parentActionSub = field.parent?.model;
            return parentAction?.typeOption !== 'calculateField' || parentActionSub.basedOn === 'COUNT';
          },
          'props.options': (field: FormlyFieldConfig) => {
            if (field.hide) return [];

            const selectedModule = this.modulesStore.modules().find(m => module.code === m.code);
            if (!selectedModule) return [];

            const excludeTypes: string[] = ['pages', 'tabs', 'list', 'linked-items', 'required-items'];

            // Get all fields from the selected module
            const allFields = this.formService.getFieldsOptionsOfModule(
              this.modulesStore.generatedForms()[selectedModule.id],
              excludeTypes,
              true,
              this.modulesStore.generatedForms(),
              this.modulesStore.moduleCodeMapper()
            );

            const basedOn = field.parent?.model?.basedOn;
            if (basedOn === 'LAST') {
              return allFields;
            }

            return allFields.filter(fieldOption => {
              const field = FormHelper.getFieldByKeyPath(
                fieldOption.value,
                this.modulesStore.generatedForms()[selectedModule.id]
              );

              if (!field) return false;

              const dataType = field.props?.datatype?.[0];
              return dataType === 'number' || field.type === 'number' || field.type === 'input-number';
            });
          },
        },
      },
    ];
  }

  getSourceSubFieldConfig(module: Module): FormlyFieldConfig {
    // Memoized so `props.options` and `props.description` share one resolution per sweep, and so
    // a stored `sourceSubField` survives a renamed subfield or an unavailable linked module
    // instead of rendering blank (same pattern as `ConditionFieldsService.subFieldOptions`).
    const sourceSubFieldOptions = (field: FormlyFieldConfig): FieldOption[] | undefined => {
      const storedValue = field.model?.sourceSubField;
      return FormHelper.memoizedFieldOptions(
        field,
        [
          field.model?.sourceField,
          storedValue,
          this.modulesStore.modules(),
          this.modulesStore.generatedForms(),
        ],
        () =>
          FormHelper.moduleLinkedFieldOptions(
            this.getField(field, 'sourceField', module.code),
            'Hele object',
            this.modulesStore,
            this.formService,
            { logUnresolvedModuleCode: true, storedValue }
          )
      );
    };

    return {
      key: 'sourceSubField',
      type: 'select',
      className: 'pl-4',
      props: {
        label: 'Selecteer sub invoerveld',
        description: '',
        placeholder: '',
        options: [],
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => {
          if (
            field.model?.basedOn === 'static' ||
            field.model?.operator === 'isnull' ||
            field.model?.operator === '!isnull'
          ) {
            return true;
          }
          const targetField = this.getField(field, 'sourceField', module.code);

          return !targetField?.props?.optionsArray?.some(
            (optionItem: OptionsItem) => optionItem.optionsType === 'module'
          );
        },
        'props.options': (field: FormlyFieldConfig) => {
          if (field.hide) {
            return [];
          }
          return sourceSubFieldOptions(field);
        },
        'props.description': (field: FormlyFieldConfig) => {
          if (field.hide) {
            return undefined;
          }
          return FormHelper.subFieldSelectionHint(sourceSubFieldOptions(field) ?? []);
        },
      },
    };
  }

  getValueFields(): FormlyFieldConfig[] {
    return [
      {
        key: 'value',
        type: 'input',
        className: 'w-max-sm',
        defaultValue: '',
        props: {
          label: 'Waarde',
          placeholder: '',
        },
        expressions: {
          hide: (field: FormlyFieldConfig) => {
            const basedField = this.formService.findFieldValue(field, 'basedOn');
            if (basedField === 'transform') {
              return false;
            }
            if (basedField !== 'static') return true;

            const targetField = this.getTargetField(field);
            if (!targetField) return true;

            const targetFieldType = this.formService.getInputOptionsOfField(targetField);
            return targetFieldType.options || targetFieldType.datatype[0] === 'boolean';
          },
        },
      },
      {
        key: 'value',
        type: 'toggle',
        className: 'w-max-sm',
        defaultValue: '',
        props: {
          label: 'Waarde',
          required: false,
          placeholder: '',
        },
        expressions: {
          hide: (field: FormlyFieldConfig) => {
            const basedField = this.formService.findFieldValue(field, 'basedOn');
            if (basedField === 'transform') {
              return false;
            }
            if (basedField !== 'static') return true;
            const targetField = this.getTargetField(field);
            if (!targetField) return true;
            const targetFieldType = this.formService.getInputOptionsOfField(targetField);
            return targetFieldType.datatype[0] !== 'boolean';
          },
        },
      },
      {
        key: 'value',
        type: 'custom-select',
        className: 'w-max-sm',
        props: {
          label: 'Waarde',
          placeholder: '',
          options: [],
        },
        expressions: {
          hide: (field: FormlyFieldConfig) => {
            const basedField = this.formService.findFieldValue(field, 'basedOn');
            if (basedField !== 'static') {
              return true;
            }
            const moduleCode = this.getModuleCode(field);
            const selectedModule = this.modulesStore.modules().find(module => module.code === moduleCode);

            if (selectedModule === undefined) {
              return true;
            }
            const keyPathOfTarget = this.formService.findFieldValue(field, 'targetField');
            const targetField = FormHelper.getFieldByKeyPath(
              keyPathOfTarget,
              this.modulesStore.generatedForms()[selectedModule.id],
              true,
              this.modulesStore.generatedForms(),
              this.modulesStore.moduleCodeMapper()
            );

            if (targetField === undefined) {
              return true;
            }

            const targetFieldType = this.formService.getInputOptionsOfField(targetField);

            return basedField !== 'static' || !targetFieldType.options;
          },
          'props.multiple': (field: FormlyFieldConfig) => {
            const moduleCode = this.getModuleCode(field);
            const selectedModule = this.modulesStore.modules().find(module => module.code === moduleCode);
            if (selectedModule === undefined) {
              return true;
            }
            const keyPathOfTarget = this.formService.findFieldValue(field, 'targetField');
            const targetField = FormHelper.getFieldByKeyPath(
              keyPathOfTarget,
              this.modulesStore.generatedForms()[selectedModule.id]
            );
            if (targetField === undefined) {
              return false;
            }
            const selectedFieldType = this.formService.getDataTypeOfType(targetField);
            return selectedFieldType?.[0] === 'array';
          },
          'props.options': (field: FormlyFieldConfig) => {
            if (field.hide) return [];
            const moduleCode = this.getModuleCode(field);
            return new Observable(subscriber => {
              const selectedModule = this.modulesStore.modules().find(module => module.code === moduleCode);

              if (!selectedModule) {
                subscriber.next([]);
                subscriber.complete();
                return;
              }

              const keyPathOfTarget = this.formService.findFieldValue(field, 'targetField');
              const targetField = FormHelper.getFieldByKeyPath(
                keyPathOfTarget,
                this.modulesStore.generatedForms()[selectedModule.id]
              );

              if (targetField.type === 'custom-userroles-multicheckbox') {
                const options = this.userRolesStore.userRoles().map(role => ({
                  label: role.name,
                  value: role.id,
                }));
                field.props.multiple = true;
                subscriber.next(options);
                subscriber.complete();
                return;
              }

              if (!targetField?.props?.optionsArray) {
                subscriber.next([]);
                subscriber.complete();
                return;
              }

              // Handle module-based options that require async loading
              const moduleOptionsRequests = targetField.props.optionsArray
                .filter(optionsItem => optionsItem.type !== null && optionsItem.optionsType === 'module')
                .map(optionsItem => {
                  const moduleId = this.modulesStore.moduleCodeToId(optionsItem.moduleCode);

                  return this.entriesService.getEntries({ moduleId: moduleId, offset: 0, limit: 100 }).pipe(
                    map(entries => {
                      const allEntries = cloneDeep(this.modulesStore.entries());
                      if (!allEntries[moduleId]) {
                        allEntries[moduleId] = entries;
                      }

                      const options = this.formService.generateOptions(
                        optionsItem,
                        this.modulesStore.generatedForms()[moduleId],
                        this.modulesStore.moduleCodeMapper(),
                        targetField.props.optionsArray.length > 1,
                        allEntries,
                        this.modulesStore.taxonomies()
                      );

                      if (optionsItem.moduleCode === 'users') {
                        options.push({ value: 'currentUser', label: 'Huidige gebruiker' });
                      }
                      if (optionsItem.moduleCode === 'companies') {
                        options.push({ value: 'currentCompany', label: 'Huidige bedrijf' });
                      }
                      if (optionsItem.moduleCode === 'locations') {
                        options.push({ value: 'currentLocation', label: 'Huidige locatie' });
                      }

                      return options;
                    })
                  );
                });

              // Handle immediate options (manual and taxonomy)
              const immediateOptions = targetField.props.optionsArray
                .filter(optionsItem => optionsItem.type !== null && optionsItem.optionsType !== 'module')
                .reduce((acc, optionsItem) => {
                  const moduleId = this.modulesStore.moduleCodeToId(optionsItem.moduleCode);
                  const options = this.formService.generateOptions(
                    optionsItem,
                    this.modulesStore.generatedForms()[moduleId],
                    this.modulesStore.moduleCodeMapper(),
                    targetField.props.optionsArray.length > 1,
                    this.modulesStore.entries(),
                    this.modulesStore.taxonomies()
                  );
                  return [...acc, ...options];
                }, []);

              if (moduleOptionsRequests.length > 0) {
                forkJoin(moduleOptionsRequests)
                  .pipe(
                    map((moduleOptionsArrays: SourceField['optionsArray']) => [
                      ...immediateOptions,
                      ...moduleOptionsArrays.flat(),
                    ]),
                    take(1)
                  )
                  .subscribe({
                    next: combinedOptions => {
                      subscriber.next(combinedOptions);
                      subscriber.complete();
                    },
                    error: error => {
                      console.error('Error fetching options:', error);
                      subscriber.next(immediateOptions);
                      subscriber.complete();
                    },
                  });
              } else {
                subscriber.next(immediateOptions);
                subscriber.complete();
              }
            });
          },
        },
      },
    ];
  }

  /**
   * @param allowExpression opt-in for basedOn:'expression'. Only the interface-actions editor passes
   * this: expressions are evaluated in the frontend mapping fill, which backend automation actions
   * and the field-level default-value mappings do not run.
   */
  getBasedOnOptions(module: Module, type: 'action' | 'interface' = 'interface', allowExpression = false) {
    const basedOnOptions = [
      { value: 'entryId', label: 'Huidige entry id' },
      { value: 'moduleId', label: 'Huidige module id' },
      { value: 'entry', label: 'Huidige entry' },
      { value: 'entrySmall', label: 'Huidige entry id + name (entry-small)' },
      { value: 'static', label: 'Vaste waarde' },
      { value: 'dynamic', label: 'Waarde van invoerveld' },
      { value: 'userInput', label: 'Door gebruiker in te voeren' },
      { value: 'currentDate', label: 'Waarde van huidige tijd en datum' },
      { value: 'stepResult', label: 'Waarde uit eerdere stap' },
    ];

    if (allowExpression && type === 'interface') {
      basedOnOptions.push({ value: 'expression', label: 'Berekende waarde (expressie)' });
    }

    if (type === 'action') {
      basedOnOptions.push({ value: 'append', label: 'Toevoegen aan array' });
      // The backend's branch is SUBTRACT; the earlier value 'substract' made every such action throw.
      basedOnOptions.push({ value: 'subtract', label: 'Verwijderen van array' });
      basedOnOptions.push({ value: 'transform', label: 'Gebruik meerder velden van de trigger entry (transform)' });
    }

    // Check if module has any field that references onboarding-forms
    const hasFormbuilderField = FormHelper.getFields(this.modulesStore.generatedForms()[module.id]).some(field => {
      return field.props?.optionsArray?.some((optionItem: OptionsItem) => optionItem.moduleCode === 'onboarding-forms');
    });

    this.showFormbuilderField = hasFormbuilderField;

    return basedOnOptions;
  }

  private getModuleCode(field: FormlyFieldConfig): string {
    return (
      field.parent?.parent?.parent?.model?.moduleCode ||
      field?.parent?.parent?.parent?.model?.optionsArray?.[0].moduleCode
    );
  }

  private getTargetField(field: FormlyFieldConfig) {
    const moduleCode = this.getModuleCode(field);
    const keyPath = this.formService.findFieldValue(field, 'targetField');
    const selectedModule = this.modulesStore.modules().find(m => m.code === moduleCode);

    if (!selectedModule || !keyPath) return null;

    return FormHelper.getFieldByKeyPath(keyPath, this.modulesStore.generatedForms()[selectedModule.id]);
  }

  getField(field: FormlyFieldConfig, searchField: string, moduleCode: string) {
    const fieldValue = this.formService.findFieldValue(field, searchField);

    if (!fieldValue) {
      return undefined;
    }

    const selectedModule = this.modulesStore.modules().find(module => module.code === moduleCode);
    return FormHelper.getFieldByKeyPath(fieldValue, this.modulesStore.generatedForms()[selectedModule.id]);
  }
}
